/**
 * Bulk-action toolbar shown above the exception table when one or more
 * rows are selected via the ``__select__`` column.
 *
 * Compact table-toolbar treatment (not a filled alert): a thin teal
 * left accent on a near-white bar, ~42px tall, sized to the table card.
 * Three zones - selection summary (left), eligibility note (middle),
 * actions (right).
 *
 * Offers the same lifecycle actions as the drawer - Cannot fix yet
 * (block), Remove block, Ignore, Reopen for review - driven by the shared
 * ``TRANSITIONS`` matrix. Each action button only renders when at least one
 * selected row is eligible for it (and "Ignore" is hidden for non-ignorable
 * types via ``allowIgnore``); ineligible rows in a mixed selection are skipped
 * server-side (per-id outcomes come back in the bulk response).
 *
 * Feedback: this component owns the dialog open state, the mutation
 * calls, AND the result toast (via ``useErrorToast``). The toast renders
 * at the app-level provider, so it survives this bar unmounting when the
 * selection clears - the parent no longer lifts a result Alert.
 */
import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ReplayIcon from '@mui/icons-material/Replay'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ReasonDialog, { type ReasonVariant } from './dialogs/ReasonDialog'
import { useExceptionLifecycle } from '../../../hooks/useExceptionLifecycle'
import type { TransitionAction } from '../../../config/exceptions/shared/transitions'
import { useErrorToast, extractErrorMessage } from '../../shared/ToastProvider'
import { poolwerxColors } from '../../../theme/colors'

export interface BulkSelectedRow {
  exception_tag_id: number
  exception_status?: string
  /** Display identity (e.g. "Sale #10293") for the confirm modal. */
  label: string
}

export interface BulkActionBannerProps {
  /** Subset of the currently-displayed rows that are checked. */
  selectedRows: BulkSelectedRow[]
  /**
   * Per-type rule: when false, the "Ignore" bulk action is hidden
   * (e.g. register-closure exceptions). Inspector tables are single-code, so
   * one value covers the whole selection. Backend re-enforces per id. */
  allowIgnore?: boolean
  /** Clear-selection click. */
  onClear: () => void
  /**
   * Called after a successful bulk mutation lands. Parent decides
   * whether to refetch / clear selection.
   */
  onAfterMutation?: () => void
  /** Sticky `top:` so the toolbar stays pinned while scrolling. */
  stickyTop?: number | string
  /** Sticky z-index (sits above the ResultsToolbar). */
  stickyZIndex?: number
  /** Forwarded outer-Box ref so the parent can measure the bar height. */
  bannerRef?: React.Ref<HTMLDivElement>
}

const exWord = (n: number) => (n === 1 ? 'exception' : 'exceptions')

// Lifecycle states any of the four actions can touch. A selected row in
// any other state (e.g. resolved) is "skipped" - no action applies.
const ACTIONABLE_STATUSES = new Set(['needs_review', 'blocked', 'ignored'])

type ActionTone = 'danger' | 'teal' | 'neutral'

interface BulkActionDef {
  action: TransitionAction
  label: string
  icon: React.ReactNode
  requiresReason: boolean
  tone: ActionTone
  /** Lifecycle statuses this action can be applied to (from TRANSITIONS). */
  eligibleStatuses: string[]
  /** Success-toast copy for n successfully-transitioned rows. */
  successMessage: (n: number) => string
}

// Order matches the drawer menu intent. Eligibility mirrors the backend
// TRANSITIONS matrix.
const ACTIONS: BulkActionDef[] = [
  {
    action: 'block',
    label: 'Cannot fix yet',
    icon: <BlockIcon fontSize="small" />,
    requiresReason: true,
    tone: 'danger',
    eligibleStatuses: ['needs_review', 'ignored'],
    successMessage: (n) => `${n} ${exWord(n)} blocked`,
  },
  {
    action: 'unblock',
    label: 'Remove block',
    icon: <LockOpenIcon fontSize="small" />,
    requiresReason: false,
    tone: 'teal',
    eligibleStatuses: ['blocked'],
    successMessage: (n) => `Block removed from ${n} ${exWord(n)}`,
  },
  {
    action: 'ignore',
    label: 'Ignore',
    icon: <VisibilityOffIcon fontSize="small" />,
    requiresReason: true,
    tone: 'neutral',
    eligibleStatuses: ['needs_review', 'blocked'],
    successMessage: (n) => `${n} ${exWord(n)} ignored`,
  },
  {
    action: 'reopen',
    label: 'Reopen for review',
    icon: <ReplayIcon fontSize="small" />,
    requiresReason: false,
    tone: 'teal',
    eligibleStatuses: ['ignored'],
    successMessage: (n) => `${n} ${exWord(n)} reopened for review`,
  },
]

// Per-tone button styling. Soft-red for the destructive block (light
// red wash, red text/border - never solid filled); teal/neutral
// outlines for the rest. Icons stay but read lighter than the label.
const toneSx: Record<ActionTone, object> = {
  danger: {
    color: 'error.main',
    borderColor: 'rgba(211,47,47,0.5)',
    bgcolor: 'rgba(211,47,47,0.06)',
    '&:hover': {
      borderColor: 'error.main',
      bgcolor: 'rgba(211,47,47,0.12)',
    },
    '& .MuiButton-startIcon': { opacity: 0.8 },
  },
  teal: {
    color: poolwerxColors.teal,
    borderColor: poolwerxColors.tealAccent,
    '&:hover': {
      borderColor: poolwerxColors.tealHover,
      bgcolor: 'rgba(0,127,152,0.04)',
    },
    '& .MuiButton-startIcon': { opacity: 0.8 },
  },
  neutral: {
    // Soft slate (blue-grey) wash + border so Ignore reads as a
    // deliberate action rather than a disabled/muted outline - mirrors
    // the block button's light-wash treatment in a neutral tone.
    color: '#334155',
    borderColor: '#CBD5E1',
    bgcolor: 'rgba(100,116,139,0.10)',
    '&:hover': {
      borderColor: '#94A3B8',
      bgcolor: 'rgba(100,116,139,0.18)',
    },
    '& .MuiButton-startIcon': { opacity: 0.85 },
  },
}

const BulkActionBanner: React.FC<BulkActionBannerProps> = ({
  selectedRows,
  allowIgnore = true,
  onClear,
  onAfterMutation,
  stickyTop,
  stickyZIndex,
  bannerRef,
}) => {
  const { bulkTransition, pending } = useExceptionLifecycle()
  const { showSuccess, showWarning, showError } = useErrorToast()
  // Pending reason-capture action (block / ignore) whose dialog is open.
  const [reasonAction, setReasonAction] = useState<BulkActionDef | null>(null)

  // Eligible tag ids per action, derived from the selection's statuses.
  const idsByAction = useMemo(() => {
    const map = {} as Record<TransitionAction, number[]>
    for (const def of ACTIONS) {
      map[def.action] = selectedRows
        .filter(
          (r) =>
            r.exception_status &&
            def.eligibleStatuses.includes(r.exception_status),
        )
        .map((r) => r.exception_tag_id)
    }
    return map
  }, [selectedRows])

  // Overall eligibility: rows actionable by at least one action vs rows
  // that no action can touch (skipped).
  const eligibleCount = useMemo(
    () =>
      selectedRows.filter(
        (r) => r.exception_status && ACTIONABLE_STATUSES.has(r.exception_status),
      ).length,
    [selectedRows],
  )
  const skippedCount = selectedRows.length - eligibleCount

  // Only surface action buttons that have at least one eligible row -
  // keeps the toolbar tight and hides e.g. Reopen unless ignored rows
  // are selected. "Ignore" is also hidden when the exception type
  // is non-ignorable (backend re-enforces per id).
  const visibleActions = ACTIONS.filter(
    (def) =>
      idsByAction[def.action].length > 0 &&
      (def.action !== 'ignore' || allowIgnore),
  )

  const runBulk = async (
    def: BulkActionDef,
    ids: number[],
    reason?: string,
  ) => {
    if (ids.length === 0) return
    try {
      const result = await bulkTransition(ids, def.action, reason)
      if (result.failed > 0) {
        showWarning(`${result.success} succeeded · ${result.failed} failed`)
      } else {
        showSuccess(def.successMessage(result.success))
      }
      onAfterMutation?.()
      onClear()
    } catch (err) {
      showError(extractErrorMessage(err))
    }
  }

  const handleClick = (def: BulkActionDef) => {
    if (def.requiresReason) {
      setReasonAction(def)
    } else {
      void runBulk(def, idsByAction[def.action])
    }
  }

  const handleReasonSubmit = async (reason: string) => {
    if (!reasonAction) return
    await runBulk(reasonAction, idsByAction[reasonAction.action], reason)
    setReasonAction(null)
  }

  // Eligible rows for the pending reason action - same predicate as
  // ``idsByAction`` so the listed identities line up exactly with the
  // ids the mutation will touch. Drives the confirm modal's "Changing
  // these N exceptions" list + skipped caption.
  const reasonEligibleRows = useMemo(() => {
    if (!reasonAction) return [] as BulkSelectedRow[]
    return selectedRows.filter(
      (r) =>
        r.exception_status &&
        reasonAction.eligibleStatuses.includes(r.exception_status),
    )
  }, [reasonAction, selectedRows])
  const reasonItems = reasonEligibleRows.map((r) => r.label)
  const reasonSkipped = selectedRows.length - reasonEligibleRows.length

  return (
    <>
      <Box
        ref={bannerRef}
        role="region"
        aria-label="Bulk selection"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          minHeight: 42,
          pl: 1.25,
          pr: 1.5,
          py: 0.5,
          mb: 1,
          borderRadius: 1,
          // Calm pale-teal toolbar (not an alert banner): tinted fill +
          // teal-tinted hairline border + a slightly stronger teal left
          // accent so the bar reads as a table affordance.
          bgcolor: '#F2FAFC',
          border: '1px solid #B9E4EA',
          borderLeft: `3px solid ${poolwerxColors.teal}`,
          ...(stickyTop !== undefined
            ? {
                position: 'sticky',
                top: stickyTop,
                zIndex: stickyZIndex,
                transition: 'top 200ms ease',
              }
            : {}),
        }}
      >
        {/* Left: selection summary chip */}
        <Chip
          size="small"
          label={`${selectedRows.length} selected`}
          sx={{
            fontWeight: 600,
            color: poolwerxColors.teal,
            bgcolor: 'rgba(0,127,152,0.08)',
            border: `1px solid ${poolwerxColors.tealAccent}`,
          }}
        />

        {/* Middle: eligibility note */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {skippedCount === 0
              ? `${eligibleCount} eligible for actions`
              : `${eligibleCount} eligible, ${skippedCount} skipped`}
          </Typography>
          {skippedCount > 0 && (
            <Tooltip
              title="Each action applies only to the selected rows in an eligible state. Ineligible rows are skipped automatically."
              arrow
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }}
              />
            </Tooltip>
          )}
        </Stack>

        <Box sx={{ flex: 1 }} />

        {/* Right: actions */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {visibleActions.map((def) => (
            <Button
              key={def.action}
              size="small"
              variant="outlined"
              startIcon={def.icon}
              onClick={() => handleClick(def)}
              disabled={pending}
              sx={{ textTransform: 'none', ...toneSx[def.tone] }}
            >
              {def.label}
            </Button>
          ))}
          <Button
            size="small"
            variant="text"
            startIcon={<CloseIcon fontSize="small" />}
            onClick={onClear}
            disabled={pending}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Clear
          </Button>
        </Stack>
      </Box>
      <ReasonDialog
        open={reasonAction !== null}
        variant={(reasonAction?.action === 'block' ? 'block' : 'ignore') as ReasonVariant}
        count={reasonItems.length}
        items={reasonItems}
        skippedCount={reasonSkipped}
        pending={pending}
        onCancel={() => setReasonAction(null)}
        onSubmit={handleReasonSubmit}
      />
    </>
  )
}

export default BulkActionBanner
